import { DelayedTrackerPropsAwaiter } from "@AppBuilderShared/shared/lib/TrackerContext";
import {
	ITrackerContext,
	ITrackerEventData,
	ITrackerEventOptions,
	TrackerMetricType,
} from "@AppBuilderShared/shared/lib/TrackerContext.types";

type CeruleoTelemetryEventType =
	| "viewer_loaded"
	| "parameter_changed"
	| "customize_completed"
	| "export_completed"
	| "error";

type CeruleoTelemetryMode = "hosted" | "embedded";

type CeruleoTelemetryEvent = {
	eventType: CeruleoTelemetryEventType;
	occurredAt: string;
	payload: Record<string, unknown>;
	shapediverSessionId?: string;
};

type CeruleoRuntimeConfig = {
	enabled: boolean;
	tokenId?: string;
	mode?: CeruleoTelemetryMode;
	endpoint?: string;
};

const FLUSH_INTERVAL_MS = 3000;
const MAX_BATCH_SIZE = 100;
const MAX_QUEUE_SIZE = 500;
// Retry backoff bounds. A failed flush must NOT retry on a fixed short interval:
// under a sustained 429/5xx that turns every viewer tab into a ~3 req/s storm
// that amplifies the very overload causing the failures. Back off exponentially
// (with jitter) and respect Retry-After, capped so events still eventually send.
const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 60000;
const DRAIN_DELAY_MS = 300;

let queue: CeruleoTelemetryEvent[] = [];
let flushTimer: number | undefined;
let flushing = false;
let unloadHandlersRegistered = false;
let viewerLoadedSent = false;
let consecutiveFailures = 0;

const runtimeConfig = resolveRuntimeConfig();

export const CeruleoTelemetryTracker: ITrackerContext = {
	trackPageview: function (
		_eventData?: ITrackerEventData,
		_options?: ITrackerEventOptions,
	): void {
		// viewer_loaded is emitted by bootstrap success.
	},
	trackEvent: function (
		eventName: string,
		options?: ITrackerEventOptions,
		_eventData?: ITrackerEventData,
	): void {
		const props = options?.props ?? {};
		const durationMs = coerceNumber(props.duration);
		const namespace = coerceString(props.namespace);
		const status = coerceString(props.status);
		const values = coerceRecord(props.values);

		switch (eventName) {
			case "parameter_changed": {
				recordCeruleoTelemetryEvent("parameter_changed", {
					namespace,
					parameterKey: namespace ?? "parameter_values",
					nextValue: values ?? {},
					values: values ?? {},
				});
				break;
			}
			case "customize_success": {
				recordCeruleoTelemetryEvent("customize_completed", {
					success: true,
					duration_ms: durationMs ?? 0,
					namespace,
				});
				break;
			}
			case "customize_error": {
				recordCeruleoTelemetryEvent("customize_completed", {
					success: false,
					duration_ms: durationMs ?? 0,
					namespace,
					status,
				});
				recordCeruleoTelemetryEvent("error", {
					code: "customize_error",
					message: status ?? "Customization failed",
					namespace,
				});
				break;
			}
			case "export_success": {
				recordCeruleoTelemetryEvent("export_completed", {
					success: true,
					duration_ms: durationMs,
					type: namespace,
				});
				break;
			}
			case "export_error": {
				recordCeruleoTelemetryEvent("export_completed", {
					success: false,
					duration_ms: durationMs,
					type: namespace,
					status,
				});
				recordCeruleoTelemetryEvent("error", {
					code: "export_error",
					message: status ?? "Export failed",
					type: namespace,
				});
				break;
			}
			default:
				break;
		}
	},
	trackMetric: function (
		_type: TrackerMetricType,
		_metricName: string,
		_value: number,
		_options?: ITrackerEventOptions,
	): void {
		// No Ceruleo telemetry mapping for metrics yet.
	},
	delayedPropsAwaiter: new DelayedTrackerPropsAwaiter(),
};

export function recordCeruleoTelemetryEvent(
	eventType: CeruleoTelemetryEventType,
	payload: Record<string, unknown>,
	options?: { shapediverSessionId?: string },
) {
	if (!runtimeConfig.enabled) {
		return;
	}

	if (eventType === "viewer_loaded") {
		if (viewerLoadedSent) {
			return;
		}
		viewerLoadedSent = true;
	}

	ensureUnloadHandlers();

	const event: CeruleoTelemetryEvent = {
		eventType,
		occurredAt: new Date().toISOString(),
		payload: sanitizePayload(payload),
		...(options?.shapediverSessionId
			? { shapediverSessionId: options.shapediverSessionId }
			: {}),
	};

	queue.push(event);
	if (queue.length > MAX_QUEUE_SIZE) {
		queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
	}

	if (queue.length >= MAX_BATCH_SIZE) {
		void flushQueue();
		return;
	}

	scheduleFlush(FLUSH_INTERVAL_MS);
}

function resolveRuntimeConfig(): CeruleoRuntimeConfig {
	try {
		const params = new URLSearchParams(window.location.search);
		const tokenId =
			params.get("ceruleoToken") ??
			params.get("token") ??
			undefined;
		const modeParam = params.get("mode");
		const mode: CeruleoTelemetryMode =
			modeParam === "embedded" ? "embedded" : "hosted";

		if (!tokenId) {
			return { enabled: false };
		}

		const apiBaseUrl = resolveApiBaseUrl(params);
		const endpoint = new URL("/api/ceruleo/telemetry", apiBaseUrl).toString();

		return {
			enabled: true,
			tokenId,
			mode,
			endpoint,
		};
	} catch {
		return { enabled: false };
	}
}

function resolveApiBaseUrl(params: URLSearchParams): string {
	const explicit = params.get("ceruleoApiBaseUrl");
	if (explicit) {
		return normalizeApiBaseUrl(explicit);
	}

	if (typeof document !== "undefined" && document.referrer) {
		try {
			return normalizeApiBaseUrl(new URL(document.referrer).origin);
		} catch {
			// fall through
		}
	}

	return normalizeApiBaseUrl(window.location.origin);
}

function scheduleFlush(delayMs: number) {
	if (flushTimer !== undefined) {
		window.clearTimeout(flushTimer);
	}

	flushTimer = window.setTimeout(() => {
		flushTimer = undefined;
		void flushQueue();
	}, delayMs);
}

async function flushQueue(options: { useBeacon?: boolean } = {}) {
	if (!runtimeConfig.enabled || !runtimeConfig.endpoint || !runtimeConfig.tokenId || !runtimeConfig.mode) {
		return;
	}
	if (queue.length === 0) {
		return;
	}

	if (options.useBeacon) {
		flushWithBeacon();
		return;
	}

	if (flushing) {
		return;
	}

	flushing = true;

	const batch = queue.splice(0, MAX_BATCH_SIZE);
	const body = JSON.stringify({
		tokenId: runtimeConfig.tokenId,
		mode: runtimeConfig.mode,
		events: batch,
	});

	let succeeded = false;
	let retryAfterMs = 0;

	try {
		const response = await fetch(runtimeConfig.endpoint, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			credentials: "omit",
			keepalive: true,
		});

		if (response.ok) {
			succeeded = true;
		} else {
			restoreBatch(batch);
			retryAfterMs = parseRetryAfterMs(response.headers.get("retry-after"));
		}
	} catch {
		restoreBatch(batch);
	} finally {
		flushing = false;
		if (succeeded) {
			consecutiveFailures = 0;
			// Drain any remainder promptly (e.g. queue exceeded one batch).
			if (queue.length > 0) {
				scheduleFlush(DRAIN_DELAY_MS);
			}
		} else {
			consecutiveFailures += 1;
			// Exponential backoff with ±20% jitter, never below Retry-After.
			const backoff = Math.min(
				BASE_RETRY_MS * 2 ** (consecutiveFailures - 1),
				MAX_RETRY_MS,
			);
			const jittered = backoff * (0.9 + Math.random() * 0.2);
			scheduleFlush(Math.max(jittered, retryAfterMs));
		}
	}
}

function parseRetryAfterMs(headerValue: string | null): number {
	if (!headerValue) {
		return 0;
	}
	// Retry-After may be delta-seconds or an HTTP date.
	const seconds = Number(headerValue);
	if (Number.isFinite(seconds)) {
		return Math.max(0, seconds * 1000);
	}
	const dateMs = Date.parse(headerValue);
	return Number.isFinite(dateMs) ? Math.max(0, dateMs - Date.now()) : 0;
}

function flushWithBeacon() {
	if (!runtimeConfig.enabled || !runtimeConfig.endpoint || !runtimeConfig.tokenId || !runtimeConfig.mode) {
		return;
	}
	if (queue.length === 0 || typeof navigator.sendBeacon !== "function") {
		return;
	}

	const batch = queue.splice(0, MAX_BATCH_SIZE);
	const body = JSON.stringify({
		tokenId: runtimeConfig.tokenId,
		mode: runtimeConfig.mode,
		events: batch,
	});
	const blob = new Blob([body], { type: "application/json" });
	const accepted = navigator.sendBeacon(runtimeConfig.endpoint, blob);

	if (!accepted) {
		restoreBatch(batch);
	}
}

function restoreBatch(batch: CeruleoTelemetryEvent[]) {
	queue = [...batch, ...queue];
	if (queue.length > MAX_QUEUE_SIZE) {
		queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
	}
}

function ensureUnloadHandlers() {
	if (unloadHandlersRegistered) {
		return;
	}
	unloadHandlersRegistered = true;

	window.addEventListener("pagehide", () => {
		void flushQueue({ useBeacon: true });
	});

	window.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			void flushQueue({ useBeacon: true });
		}
	});
}

function sanitizePayload(input: Record<string, unknown>): Record<string, unknown> {
	try {
		const safe = JSON.parse(JSON.stringify(input ?? {}));
		return typeof safe === "object" && safe ? safe : {};
	} catch {
		return {};
	}
}

function coerceNumber(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string" && value.length > 0) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

function coerceString(value: unknown): string | null {
	return typeof value === "string" && value.length > 0 ? value : null;
}

function coerceRecord(
	value: unknown,
): Record<string, unknown> | null {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
}

function normalizeApiBaseUrl(value: string): string {
	try {
		const parsed = new URL(value);
		const isLocalHost =
			parsed.hostname === "localhost" ||
			parsed.hostname === "127.0.0.1" ||
			parsed.hostname === "::1";

		if (isLocalHost && parsed.port === "3000") {
			parsed.port = "3001";
		}

		return parsed.origin;
	} catch {
		return value.replace(/\/+$/, "");
	}
}
